import Layout from "../../components/layout/Layout";
import React from "react";


export default function PrivacyPolicy(props) {
  const {
    companyName = "Predixio Technologies Ltd.",
    productName = "Predixio",
    domain = "predixio.com",
    contactEmail = "privacy@predixio.com",
    dpoEmail = "dpo@predixio.com",
    euRepEmail = "eu-rep@predixio.com",
    ukRepEmail = "uk-rep@predixio.com",
    lastUpdated = new Date().toISOString().slice(0, 10),
  } = props;

  const ui = {
    page: { background: "#f7f7f9", minHeight: "100vh", padding: "40px 16px" },
    container: {
      maxWidth: 1400,
      margin: "0 auto",
      display: "grid",
      gap: 24,
      boxSizing: "border-box",
    },
    card: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,.08)",
      borderRadius: 14,
      boxShadow: "0 8px 24px rgba(0,0,0,.05)",
      overflow: "hidden",
    },
    sidebar: { padding: 16 },
    main: { padding: 28 },
    title: { margin: 0, fontSize: 28, fontWeight: 800, color: "#0f172a" },
    subtitle: { marginTop: 6, fontSize: 14, color: "#6b7280" },
    sectionTitle: { fontSize: 18, fontWeight: 800, margin: "28px 0 10px", color: "#111827" },
    p: { margin: "10px 0", color: "#334155", lineHeight: 1.7 },
    li: { margin: "6px 0", color: "#334155", lineHeight: 1.6 },
    badge: {
      display: "inline-block",
      fontSize: 12,
      color: "#16a34a",
      background: "rgba(22,163,74,.10)",
      border: "1px solid rgba(22,163,74,.25)",
      padding: "4px 10px",
      borderRadius: 999,
      marginRight: 8,
    },
    hr: { border: "none", borderTop: "1px solid rgba(0,0,0,.06)", margin: "20px 0" },
    tocTitle: { fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 8 },
    tocLink: { display: "block", fontSize: 13, color: "#334155", padding: "6px 8px", borderRadius: 8, textDecoration: "none" },
    tocLinkActive: { background: "rgba(22,163,74,.08)", color: "#15803d", border: "1px solid rgba(22,163,74,.25)" },
    callout: {
      background: "linear-gradient(180deg, #fff, #fafafa)",
      border: "1px dashed rgba(0,0,0,.15)",
      borderRadius: 12,
      padding: 12,
      color: "#374151",
      fontSize: 13,
    },
    dl: { display: "grid", gridTemplateColumns: "220px 1fr", gap: 8 },
    code: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      fontSize: 12,
      background: "#f3f4f6",
      borderRadius: 6,
      padding: "2px 6px",
      wordBreak: "break-word",
    },
  };

  const sections = [
    { id: "intro", label: "1. About this Notice" },
    { id: "controller", label: "2. Data Controller & Contacts" },
    { id: "scope", label: "3. Scope & Applicability" },
    { id: "collection", label: "4. Data We Collect" },
    { id: "use", label: "5. How We Use Data (Purposes & Legal Bases)" },
    { id: "cookies", label: "6. Cookies & Similar Technologies" },
    { id: "sharing", label: "7. Sharing & Disclosures" },
    { id: "ads-analytics", label: "8. Analytics, Ads & Personalization" },
    { id: "security", label: "9. Security" },
    { id: "retention", label: "10. Data Retention" },
    { id: "intl", label: "11. International Transfers" },
    { id: "rights", label: "12. Your Privacy Rights (GDPR/UK GDPR)" },
    { id: "ccpa", label: "13. California (CCPA/CPRA)" },
    { id: "children", label: "14. Children’s Privacy" },
    { id: "changes", label: "15. Changes to this Notice" },
    { id: "contact", label: "16. How to Contact Us" },
  ];

  const [active, setActive] = React.useState(sections[0].id);
  React.useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 1] }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const A = ({ href, children }) => (
    <a href={href} style={{ color: "#15803d", textDecoration: "none", wordBreak: "break-word" }}>
      {children}
    </a>
  );

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Privacy Policy">
      <style>{`
        .pp-page, .pp-container, .pp-card, .pp-main {
          box-sizing: border-box; max-width: 100%; overflow-x: clip;
        }

        /* Desktop: 2 columns */
        .pp-container {
          display: grid;
          grid-template-columns: 280px 1fr;
          grid-template-areas: "sidebar main";
          gap: 24px;
        }
        .pp-sidebar { grid-area: sidebar; position: sticky; top: 24px; align-self: start; }
        .pp-main    { grid-area: main; }
        .pp-toc a { white-space: normal; }

        /* Ensure media/content don't overflow */
        .pp-main img, .pp-main table, .pp-main code { max-width: 100%; }
        .pp-table-wrap { overflow-x: auto; }

        /* Tablet */
        @media (max-width: 1024px) {
          .pp-container { grid-template-columns: 240px 1fr; gap: 20px; }
        }

        /* Mobile: stack one after the other (sidebar -> content) */
        @media (max-width: 768px) {
          .pp-container {
            grid-template-columns: 1fr;
            grid-template-areas:
              "sidebar"
              "main";
            gap: 16px;
          }
          .pp-sidebar { position: static; top: auto; width: 100%; }
          .pp-title { font-size: 22px !important; }
        }
      `}</style>

      <div className="pp-page" style={ui.page}>
        <div className="pp-container" style={ui.container}>
          <aside className="pp-card pp-sidebar" style={{ ...ui.card, ...ui.sidebar }}>
            <div style={ui.tocTitle}>Privacy Policy</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
              Last updated: <span style={{ fontWeight: 700 }}>{lastUpdated}</span>
            </div>
            <hr style={ui.hr} />
            <nav className="pp-toc">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  style={{ ...ui.tocLink, ...(active === s.id ? ui.tocLinkActive : null) }}
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          <main className="pp-card pp-main" style={{ ...ui.card, ...ui.main }}>
            <div style={{ marginBottom: 8 }}>
              <span style={ui.badge}>{productName}</span>
              <span style={ui.badge}>Privacy</span>
            </div>
            <h1 className="pp-title" style={ui.title}>Privacy Policy</h1>
            <p style={ui.subtitle}>
              This Privacy Policy explains how {companyName} (“we”, “us”, “our”) collects, uses, discloses, and safeguards personal data when you use {productName} at <strong>{domain}</strong>, our apps, and APIs (the “Service”).
            </p>
            <div style={ui.callout}>
              <strong>Summary.</strong> We collect account data, usage analytics, device and cookie identifiers, and (if applicable) payment and support data. We use this to provide and improve the Service, customize experiences, secure our systems, and comply with law. Your rights depend on your location and the laws that apply.
            </div>

            <section id="intro"><h2 style={ui.sectionTitle}>1. About this Notice</h2><p style={ui.p}>
              This Notice applies to personal data processed by {companyName} when you access or use the Service. It does not cover third-party websites or services that we do not control. Please read this Notice together with our <A href="/terms">Terms & Conditions</A>.
            </p></section><hr style={ui.hr} />

            <section id="controller"><h2 style={ui.sectionTitle}>2. Data Controller & Contacts</h2>
              <p style={ui.p}>
                For users in the EEA/UK, {companyName} is the data controller for personal data processed via the Service. You can contact us at <A href={`mailto:${contactEmail}`}>{contactEmail}</A>. If appointed, you may also reach our Data Protection Officer at <A href={`mailto:${dpoEmail}`}>{dpoEmail}</A>.
              </p>
              <p style={ui.p}>
                EU/UK representatives (if applicable): EU: <A href={`mailto:${euRepEmail}`}>{euRepEmail}</A> • UK: <A href={`mailto:${ukRepEmail}`}>{ukRepEmail}</A>.
              </p>
            </section><hr style={ui.hr} />

            <section id="scope"><h2 style={ui.sectionTitle}>3. Scope & Applicability</h2>
              <p style={ui.p}>This Notice applies to personal data we process about visitors, registered users, API consumers, and subscribers. It covers both online and related offline interactions (e.g., support).</p>
            </section><hr style={ui.hr} />

            <section id="collection"><h2 style={ui.sectionTitle}>4. Data We Collect</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}><strong>Account & Profile:</strong> name, email, username, password (hashed), preferences, subscription plan.</li>
                <li style={ui.li}><strong>Payment:</strong> billing email, cardholder name, card details (processed by our payment processor), purchase history, VAT info.</li>
                <li style={ui.li}><strong>Usage & Activity:</strong> pages viewed, fixtures searched, leagues followed, API endpoints used, timestamps, referral URLs.</li>
                <li style={ui.li}><strong>Device & Log:</strong> IP, device IDs, browser, OS, language, time zone, crash logs.</li>
                <li style={ui.li}><strong>Cookies & Identifiers:</strong> session cookies, analytics IDs, advertising IDs, A/B testing and feature flags.</li>
                <li style={ui.li}><strong>Support & Feedback:</strong> messages, attachments, contact details.</li>
                <li style={ui.li}><strong>Marketing Preferences:</strong> opt-ins, communication settings.</li>
              </ul>
              <p style={ui.p}>We may aggregate or de-identify data for analytics and product improvement. Aggregated data is not considered personal data.</p>
            </section><hr style={ui.hr} />

            <section id="use"><h2 style={ui.sectionTitle}>5. How We Use Data (Purposes & Legal Bases)</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}><strong>Provide the Service</strong> — Contract.</li>
                <li style={ui.li}><strong>Personalize & improve</strong> — Legitimate Interests / Consent.</li>
                <li style={ui.li}><strong>Communicate</strong> — Contract / Legal Obligation.</li>
                <li style={ui.li}><strong>Market</strong> — Consent / Legitimate Interests.</li>
                <li style={ui.li}><strong>Secure & prevent abuse</strong> — Legitimate Interests / Legal Obligation.</li>
                <li style={ui.li}><strong>Comply with law</strong> — Legal Obligation.</li>
              </ul>
            </section><hr style={ui.hr} />

            <section id="cookies"><h2 style={ui.sectionTitle}>6. Cookies & Similar Technologies</h2>
              <p style={ui.p}>We use essential, analytics, functional, and (if used) advertising cookies. Where required, we obtain consent via a cookie banner. Manage preferences in your browser or our cookie page (<A href="/cookies">/cookies</A>).</p>
            </section><hr style={ui.hr} />

            <section id="sharing"><h2 style={ui.sectionTitle}>7. Sharing & Disclosures</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}><strong>Vendors/Processors</strong> — hosting, analytics, support, email, payments.</li>
                <li style={ui.li}><strong>Integration Partners</strong> — football data providers/APIs.</li>
                <li style={ui.li}><strong>Legal/Compliance</strong> — to comply with law, enforce Terms, protect security or rights.</li>
                <li style={ui.li}><strong>Business transfers</strong> — merger, acquisition, financing, or asset sale.</li>
              </ul>
              <p style={ui.p}>We do not sell personal information. If we “share” for targeted ads under CPRA, we’ll disclose and provide opt-outs.</p>
            </section><hr style={ui.hr} />

            <section id="ads-analytics"><h2 style={ui.sectionTitle}>8. Analytics, Ads & Personalization</h2>
              <p style={ui.p}>We may use analytics (e.g., GA/Amplitude) and, if applicable, ad partners. These may set cookies or collect identifiers subject to your consent where required.</p>
            </section><hr style={ui.hr} />

            <section id="security"><h2 style={ui.sectionTitle}>9. Security</h2>
              <p style={ui.p}>We apply administrative, technical, and physical safeguards appropriate to the data. No method is 100% secure; we notify as required.</p>
            </section><hr style={ui.hr} />

            <section id="retention"><h2 style={ui.sectionTitle}>10. Data Retention</h2>
              <p style={ui.p}>We keep data as long as needed for the Service, legal obligations, dispute resolution, and enforcement. Retention varies by type.</p>
            </section><hr style={ui.hr} />

            <section id="intl"><h2 style={ui.sectionTitle}>11. International Transfers</h2>
              <p style={ui.p}>We may transfer data globally with appropriate safeguards (e.g., SCCs) where required.</p>
            </section><hr style={ui.hr} />

            <section id="rights"><h2 style={ui.sectionTitle}>12. Your Privacy Rights (GDPR/UK GDPR)</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}>Access, correction, deletion.</li>
                <li style={ui.li}>Object/restrict certain processing.</li>
                <li style={ui.li}>Data portability.</li>
                <li style={ui.li}>Withdraw consent.</li>
                <li style={ui.li}>Complain to a supervisory authority.</li>
              </ul>
              <p style={ui.p}>To exercise rights, email <A href={`mailto:${contactEmail}`}>{contactEmail}</A>. We may verify identity.</p>
            </section><hr style={ui.hr} />

            <section id="ccpa"><h2 style={ui.sectionTitle}>13. California (CCPA/CPRA)</h2>
              <p style={ui.p}>California residents may have rights to know/access, correction, deletion, portability, and opt out of “sharing.” We do not sell personal info as defined by CCPA.</p>
            </section><hr style={ui.hr} />

            <section id="children"><h2 style={ui.sectionTitle}>14. Children’s Privacy</h2>
              <p style={ui.p}>Not directed to children under 13. If a child’s data is provided, contact us to delete.</p>
            </section><hr style={ui.hr} />

            <section id="changes"><h2 style={ui.sectionTitle}>15. Changes to this Notice</h2>
              <p style={ui.p}>We may update this policy; material changes will be communicated. “Last updated” reflects the latest revision.</p>
            </section><hr style={ui.hr} />

            <section id="contact"><h2 style={ui.sectionTitle}>16. How to Contact Us</h2>
              <p style={ui.p}>Questions or requests: <A href={`mailto:${contactEmail}`}>{contactEmail}</A>. DPO: <A href={`mailto:${dpoEmail}`}>{dpoEmail}</A>.</p>
              <div style={{ ...ui.callout, marginTop: 12 }}>
                <div style={ui.dl}>
                  <div>Service</div><div>{productName} ({domain})</div>
                  <div>Company</div><div>{companyName}</div>
                  <div>Contact</div><div><A href={`mailto:${contactEmail}`}>{contactEmail}</A></div>
                  <div>DPO</div><div><A href={`mailto:${dpoEmail}`}>{dpoEmail}</A></div>
                  <div>EU/UK Representatives</div><div>EU: <A href={`mailto:${euRepEmail}`}>{euRepEmail}</A> • UK: <A href={`mailto:${ukRepEmail}`}>{ukRepEmail}</A></div>
                  <div>Last updated</div><div>{lastUpdated}</div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </Layout>
  );
}
